import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Building2, Loader2 } from 'lucide-react'
import { organizationApi } from '@/services/organization-api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Textarea } from '@/components/ui/textarea'

interface OrgItem {
  id: string
  name: string
  slug: string
  industry?: string
}

export default function BrowseOrgsStep() {
  const [items, setItems] = useState<OrgItem[]>([])
  const [query, setQuery] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [requestingId, setRequestingId] = useState<string | null>(null)
  const navigate = useNavigate()

  const load = async (q?: string) => {
    setIsLoading(true)
    try {
      const orgs = await organizationApi.listPublic(q)
      setItems(orgs)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const requestJoin = async (orgId: string) => {
    setError(null)
    setRequestingId(orgId)
    try {
      await organizationApi.createJoinRequest(orgId, message)
      navigate('/onboarding/pending')
    } catch {
      setError('Could not submit join request')
    } finally {
      setRequestingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Browse Organizations</h2>
        <p className="text-muted-foreground mt-1">Find and request to join an existing organization</p>
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <Label htmlFor="search" className="sr-only">Search</Label>
          <Input
            id="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search organizations..."
          />
        </div>
        <Button onClick={() => load(query)}>
          <Search className="mr-2 h-4 w-4" />
          Search
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Request Message (Optional)</Label>
        <Textarea
          id="message"
          placeholder="Add a message to your join request"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No organizations found</h3>
            <p className="text-muted-foreground">Try a different search or create your own organization</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((org) => (
            <Card key={org.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{org.name}</p>
                    <p className="text-sm text-muted-foreground">{org.slug}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => requestJoin(org.id)}
                  disabled={requestingId === org.id}
                >
                  {requestingId === org.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Request Join'
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
